/**
 * Helpers for jigsaw library cards — maps JigsawPlayRo to the card shape used by BrowseStylePuzzleCard.
 */

/** Prefer English title, then French. */
export function getJigsawStoryTitle(story) {
    if (!story || typeof story !== "object") return "";
    const en = story.english?.title ?? story.en?.title;
    const fr = story.french?.title ?? story.fr?.title;
    const enStr = typeof en === "string" ? en.trim() : "";
    const frStr = typeof fr === "string" ? fr.trim() : "";
    return enStr || frStr || "";
}

/** Story thumbnail for landing / browse cards. */
export function pickJigsawCardImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const keys = [
        "imageIcon400Url",
        "image_icon_400_url",
        "imageIcon200Url",
        "image_icon_200_url",
        "imageUrl",
        "image_url",
        "imagePreviewUrl",
        "image_preview_url",
    ];
    for (const k of keys) {
        const u = story[k];
        if (typeof u === "string" && u.trim()) return u.trim();
    }
    return null;
}

/** Square story art for library cards — full image or icons, never the wide preview banner. */
export function pickJigsawSquareImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const keys = [
        "imageUrl",
        "image_url",
        "imageIcon400Url",
        "image_icon_400_url",
        "imageIcon200Url",
        "image_icon_200_url",
    ];
    for (const k of keys) {
        const u = story[k];
        if (typeof u === "string" && u.trim()) return u.trim();
    }
    return null;
}

/** Parse grid size from names like `T09-S6400-T22-J03` or `T32-S6400-…`. */
function parseJigsawTileSizeFromName(name) {
    const trimmed = String(name ?? "").trim();
    if (!trimmed) return 0;
    const tilePart = (trimmed.split("-")[0] || "").trim();
    const match = tilePart.match(/^T(\d+)$/i);
    if (match) {
        const n = parseInt(match[1], 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }
    const legacy = parseInt(String(tilePart).replace(/^T0?/i, ""), 10);
    return Number.isFinite(legacy) && legacy > 0 ? legacy : 0;
}

/** Infer N×N grid side from `piecePaths.pieces.length` when it is a perfect square. */
function tileSizeFromPiecePaths(template) {
    const pieces =
        template?.piecePaths?.pieces ??
        template?.piece_paths?.pieces ??
        null;
    if (!Array.isArray(pieces) || pieces.length === 0) return 0;
    const n = Math.round(Math.sqrt(pieces.length));
    return n > 0 && n * n === pieces.length ? n : 0;
}

/** Public play grid sizes shown on the story difficulty picker (order matters). */
export const JIGSAW_PLAY_TILE_SIZES = [6, 9, 12, 16, 24, 32];

function isStandardJigsawPlayTileSize(tileSize) {
    return JIGSAW_PLAY_TILE_SIZES.includes(Number(tileSize));
}

/** Seed segment from names like `T09-S6400-T22-J03` → `S6400`. */
export function getJigsawTemplateSeed(name) {
    const trimmed = String(name ?? "").trim();
    if (!trimmed) return "other";
    const parts = trimmed.split("-");
    if (parts.length >= 2 && parts[1].startsWith("S")) return parts[1];
    return "other";
}

/** Prefer the seed used by most templates on this story (keeps 6–32 rows on one cut set). */
export function pickPrimaryJigsawSeed(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const counts = {};
    for (const t of templates) {
        const seed = getJigsawTemplateSeed(t.templateName ?? t.template_name ?? t.name);
        counts[seed] = (counts[seed] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

export function pickJigsawTemplateForTileSize(templates, tileSize, preferredSeed = null) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const ts = Number(tileSize);
    if (!Number.isFinite(ts) || ts <= 0) return null;
    if (preferredSeed) {
        const seeded = templates.find(
            (t) =>
                resolveJigsawTemplateTileSize(t) === ts &&
                getJigsawTemplateSeed(t.templateName ?? t.template_name ?? t.name) === preferredSeed,
        );
        if (seeded) return seeded;
    }
    return templates.find((t) => resolveJigsawTemplateTileSize(t) === ts) ?? null;
}

/** Merge story templates with any jigsaw rows referenced only in `userPlays`. */
export function mergeJigsawPlayTemplates(templates, userPlays) {
    const list = Array.isArray(templates) ? [...templates] : [];
    const seen = new Set(list.map((t) => t?.id).filter(Boolean));
    for (const play of userPlays ?? []) {
        const jigsaw = play?.jigsaw;
        if (jigsaw?.id && !seen.has(jigsaw.id)) {
            list.push(jigsaw);
            seen.add(jigsaw.id);
        }
    }
    return list;
}

/** One row per standard size (6–32); `template` is null when not linked on this story yet. */
export function buildJigsawPlayLevelEntries(templates) {
    const list = Array.isArray(templates) ? templates : [];
    const primarySeed = pickPrimaryJigsawSeed(list);
    return JIGSAW_PLAY_TILE_SIZES.map((tileSize) => ({
        tileSize,
        template: pickJigsawTemplateForTileSize(list, tileSize, primarySeed),
    }));
}

/** Default play selection: Medium (9×9) when linked, otherwise nearest available size. */
export function pickDefaultJigsawPlayTemplate(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const primarySeed = pickPrimaryJigsawSeed(templates);
    const preferredOrder = [9, 6, 12, 16, 24, 32];
    for (const tileSize of preferredOrder) {
        const match = pickJigsawTemplateForTileSize(templates, tileSize, primarySeed);
        if (match) return match;
    }
    return templates
        .slice()
        .sort((a, b) => resolveJigsawTemplateTileSize(a) - resolveJigsawTemplateTileSize(b))[0];
}

/** Jigsaw tier from tile size (N×N grid side). */
export const JIGSAW_DIFFICULTY_BY_TILE = {
    6: "EASY",
    9: "MEDIUM",
    12: "HARD",
    16: "PRO",
    24: "ELITE",
    32: "INSANE",
};

export const JIGSAW_DIFFICULTY_LABELS = {
    EASY: "Easy",
    MEDIUM: "Medium",
    HARD: "Hard",
    PRO: "Pro",
    ELITE: "Elite",
    INSANE: "Insane",
};

export const JIGSAW_DIFFICULTY_ORDER = {
    EASY: 0,
    MEDIUM: 1,
    HARD: 2,
    PRO: 3,
    ELITE: 4,
    INSANE: 5,
};

/** i18n key under `puzzle.card` for a jigsaw difficulty level. */
export function getJigsawDifficultyLabelKey(level) {
    const key = String(level ?? "").toUpperCase();
    if (key === "EASY") return "puzzle.card.difficulty-easy";
    if (key === "MEDIUM") return "puzzle.card.difficulty-medium";
    if (key === "HARD") return "puzzle.card.difficulty-hard";
    if (key === "PRO") return "puzzle.card.difficulty-pro";
    if (key === "ELITE") return "puzzle.card.difficulty-elite";
    if (key === "INSANE") return "puzzle.card.difficulty-insane";
    return null;
}

export function difficultyLevelForJigsawTileSize(tileSize) {
    const s = Number(tileSize);
    if (Number.isFinite(s) && s > 0 && JIGSAW_DIFFICULTY_BY_TILE[s]) {
        return JIGSAW_DIFFICULTY_BY_TILE[s];
    }
    if (!Number.isFinite(s) || s <= 0) return "MEDIUM";
    if (s <= 6) return "EASY";
    if (s <= 9) return "MEDIUM";
    if (s <= 12) return "HARD";
    if (s <= 16) return "PRO";
    if (s <= 24) return "ELITE";
    return "INSANE";
}

function tileSizeFromDifficultyLevel(level) {
    const key = String(level ?? "").toUpperCase();
    if (key === "EASY") return 6;
    if (key === "MEDIUM") return 9;
    if (key === "HARD") return 12;
    if (key === "PRO") return 16;
    if (key === "ELITE") return 24;
    if (key === "INSANE") return 32;
    return 0;
}

/** Grid size for tier checks and play — prefers `T09` name prefix, then API field, then difficulty. */
export function resolveJigsawTemplateTileSize(template) {
    if (!template || typeof template !== "object") return 0;
    const name = String(
        template.templateName ?? template.template_name ?? template.name ?? "",
    ).trim();
    const fromName = parseJigsawTileSizeFromName(name);
    const direct = Number(template.tileSize ?? template.tile_size);
    const fromField = Number.isFinite(direct) && direct > 0 ? direct : 0;
    const fromPieces = tileSizeFromPiecePaths(template);
    const fromLevel = tileSizeFromDifficultyLevel(
        template.difficultyLevel ?? template.difficulty_level,
    );

    if (fromName > 0) return fromName;
    if (fromField > 0) {
        if (
            fromPieces > 0 &&
            fromPieces !== fromField &&
            isStandardJigsawPlayTileSize(fromPieces)
        ) {
            return fromPieces;
        }
        return fromField;
    }
    if (fromPieces > 0) return fromPieces;
    return fromLevel;
}

function resolveLandingJigsawTemplate(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const match = (tileSize, level) =>
        templates.find(
            (t) =>
                resolveJigsawTemplateTileSize(t) === tileSize &&
                (!level || String(t?.difficultyLevel || "").toUpperCase() === level),
        );
    return (
        match(6, "EASY") ||
        match(6, "MEDIUM") ||
        match(6, "HARD") ||
        match(6, null) ||
        templates.find((t) => resolveJigsawTemplateTileSize(t) === 6) ||
        match(9, "MEDIUM") ||
        templates.find((t) => resolveJigsawTemplateTileSize(t) === 9) ||
        templates.find((t) => String(t?.difficultyLevel || "").toUpperCase() === "EASY") ||
        templates[0]
    );
}

/** Smallest published template that can offer color baskets (Medium 9×9+). */
export function resolveBasketCapableJigsawTemplate(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const capable = templates
        .filter((t) => resolveJigsawTemplateTileSize(t) >= 9 && t?.id)
        .sort(
            (a, b) => resolveJigsawTemplateTileSize(a) - resolveJigsawTemplateTileSize(b),
        );
    return capable[0] ?? null;
}

/** 12×12 jigsaw row for social share (Facebook, Instagram, etc.) — og:preview and `/share/JIGSAW/{id}`. */
export function resolveJigsawShareTemplate(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const match = (tileSize, level) =>
        templates.find(
            (t) =>
                resolveJigsawTemplateTileSize(t) === tileSize &&
                (!level || String(t?.difficultyLevel || "").toUpperCase() === level),
        );
    return (
        match(12, "MEDIUM") ||
        match(12, "HARD") ||
        match(12, "EASY") ||
        match(12, null) ||
        templates.find((t) => resolveJigsawTemplateTileSize(t) === 12) ||
        null
    );
}

/**
 * Play / browse difficulty from grid size (overrides stale API labels).
 * 6×6 → EASY; 9×9 → MEDIUM; 12×12 → HARD; 16×16 → PRO; 24×24 → ELITE; 32×32 → INSANE.
 */
export function inferJigsawDifficultyLevel(tileSize, explicitLevel) {
    const s = Number(tileSize);
    if (Number.isFinite(s) && s > 0) {
        return difficultyLevelForJigsawTileSize(s);
    }
    const key = String(explicitLevel || "").toUpperCase();
    if (JIGSAW_DIFFICULTY_LABELS[key]) return key;
    return "MEDIUM";
}

/** Format template name like "T09-S8900-T22-J03" → "9×9" */
export function formatJigsawGridSizeLabel(template) {
    const name = String(template?.templateName ?? template?.template_name ?? template?.name ?? "").trim();
    if (!name) return "";
    const parts = name.split("-");
    const tilePart = parts[0] || "";
    const tileNum = parseInt(String(tilePart).replace(/^T0?/i, ""), 10);
    if (!Number.isFinite(tileNum) || tileNum <= 0) return "";
    return `${tileNum}×${tileNum}`;
}

/** Piece count for a template (paths length, else N×N from resolved tile size). */
export function resolveJigsawPieceCount(template, tileSizeFallback = 0) {
    const pieces =
        template?.piecePaths?.pieces ??
        template?.piece_paths?.pieces ??
        null;
    if (Array.isArray(pieces) && pieces.length > 0) {
        return pieces.length;
    }
    const tile =
        resolveJigsawTemplateTileSize(template) ||
        Number(tileSizeFallback) ||
        0;
    if (Number.isFinite(tile) && tile > 0) {
        return tile * tile;
    }
    return 0;
}

/** Play picker label: "36 pieces" instead of "6×6". */
export function formatJigsawPieceCountLabel(template, tileSizeFallback = 0) {
    const count = resolveJigsawPieceCount(template, tileSizeFallback);
    if (!Number.isFinite(count) || count <= 0) return "";
    if (count === 1) return "1 piece";
    return `${count} pieces`;
}

/** Chip colors + i18n keys for template cards on the play select page. */
export function getJigsawDifficultyDisplayInfo(tileSize, explicitLevel) {
    const level = inferJigsawDifficultyLevel(tileSize, explicitLevel);
    if (level === "EASY") {
        return {
            level,
            levelKey: "puzzle.card.difficulty-easy",
            chipColor: "success",
            difficultyColor: "success",
        };
    }
    if (level === "MEDIUM") {
        return {
            level,
            levelKey: "puzzle.card.difficulty-medium",
            chipColor: "warning",
            difficultyColor: "warning",
        };
    }
    if (level === "HARD") {
        return {
            level,
            levelKey: "puzzle.card.difficulty-hard",
            chipColor: "error",
            difficultyColor: "error",
        };
    }
    if (level === "PRO") {
        return {
            level,
            levelKey: "puzzle.card.difficulty-pro",
            chipColor: "info",
            difficultyColor: "info",
        };
    }
    if (level === "ELITE") {
        return {
            level,
            levelKey: "puzzle.card.difficulty-elite",
            chipColor: "secondary",
            difficultyColor: "secondary",
        };
    }
    if (level === "INSANE") {
        return {
            level,
            levelKey: "puzzle.card.difficulty-insane",
            chipColor: "error",
            difficultyColor: "error",
        };
    }
    return {
        level: "MEDIUM",
        levelKey: "puzzle.card.difficulty-medium",
        chipColor: "warning",
        difficultyColor: "warning",
    };
}

/** Published timestamp for shelf / landing captions. */
export function getJigsawGroupPublishedAt(group) {
    const sj = group?.storyJigsaw ?? group?.story_jigsaw;
    if (sj && typeof sj === "object") {
        const fromSj = sj.publishedAt ?? sj.published_at ?? null;
        if (fromSj != null && String(fromSj).trim() !== "") return fromSj;
    }
    const fromTemplate = group?.puzzleTemplate?.publishedAt ?? group?.puzzleTemplate?.published_at ?? null;
    if (fromTemplate != null && String(fromTemplate).trim() !== "") return fromTemplate;
    const story = group?.story;
    return story?.publishedAt ?? story?.published_at ?? null;
}

/**
 * Story id, title, and default play preview (Easy 6×6 when available) for landing hero.
 */
export function getJigsawLandingFeaturedMeta(group) {
    const storyId =
        (typeof group?.storyId === "string" && group.storyId.trim()) ||
        (typeof group?.story?.id === "string" && group.story.id.trim()) ||
        null;
    if (!storyId) return null;
    const story = group?.story ?? null;
    const title = getJigsawStoryTitle(story) || storyId;
    const tpl = resolveLandingJigsawTemplate(group?.templates);
    const basketTpl = resolveBasketCapableJigsawTemplate(group?.templates);
    const tileSize = resolveJigsawTemplateTileSize(tpl) || 6;
    const level = inferJigsawDifficultyLevel(tileSize, tpl?.difficultyLevel);
    const imageUrl = pickJigsawCardImageUrl(story);
    const jigsawId =
        (typeof tpl?.id === "string" && tpl.id.trim()) || null;
    const basketJigsawId =
        (typeof basketTpl?.id === "string" && basketTpl.id.trim()) || null;
    return {
        storyId,
        title,
        tileSize,
        level,
        imageUrl: imageUrl && String(imageUrl).trim() ? String(imageUrl).trim() : null,
        publishedAt: getJigsawGroupPublishedAt(group),
        piecePaths: tpl?.piecePaths?.pieces ?? null,
        svgPath: tpl?.svgPath ?? null,
        ...(jigsawId ? { jigsawId } : {}),
        ...(basketJigsawId ? { basketJigsawId } : {}),
        basketTileSize: basketTpl ? resolveJigsawTemplateTileSize(basketTpl) : null,
    };
}

/**
 * Map a GET `/home/jigsaw-play` row (JigsawPlayRo) to the card shape used by BrowseStylePuzzleCard.
 * Story id drives `/puzzle/play-jigsaw/:storyId`; include `jigsawId` for `/board/:jigsawId`.
 */
export function parseJigsawPlayJsonValue(value) {
    if (value == null) return null;
    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    return value;
}

/** Saved on-board / tray layout from a jigsaw play session row. */
export function jigsawPlayLayoutFromRo(ro) {
    const boardRaw = parseJigsawPlayJsonValue(ro?.boardPositions ?? ro?.board_positions);
    const scatterRaw = parseJigsawPlayJsonValue(ro?.scatteredPieces ?? ro?.scattered_pieces);
    return {
        boardPositions: Array.isArray(boardRaw) ? boardRaw : [],
        scatteredPieces:
            scatterRaw && typeof scatterRaw === "object" && !Array.isArray(scatterRaw) ? scatterRaw : {},
    };
}

/** Piece path list for CanvasBoard / overlays. */
export function normalizeJigsawPiecePaths(source) {
    const raw = source?.piecePaths?.pieces ?? source?.piecePaths ?? null;
    if (!Array.isArray(raw)) return null;
    return raw.map((piece) => ({
        ...piece,
        path: piece?.path ?? piece?.d ?? null,
    }));
}

/**
 * Map a GET `/home/jigsaw-play` row (JigsawPlayRo) to the card shape used by BrowseStylePuzzleCard.
 * Story id drives `/puzzle/play-jigsaw/:storyId`; include `jigsawId` for `/board/:jigsawId`.
 */
export function jigsawPlayRoToCardPuzzle(ro) {
    if (!ro || typeof ro !== "object") return null;

    const story = ro.story ?? null;
    const storyId =
        (typeof story?.id === "string" && story.id.trim()) ||
        (typeof ro.storyId === "string" && ro.storyId.trim()) ||
        null;
    if (!storyId) return null;

    const jigsaw = ro.jigsaw ?? null;
    const storyJigsaw = ro.storyJigsaw ?? null;

    const jigsawId =
        (typeof jigsaw?.id === "string" && jigsaw.id.trim()) ||
        (typeof ro.jigsawId === "string" && ro.jigsawId.trim()) ||
        (typeof ro.jigsaw_id === "string" && ro.jigsaw_id.trim()) ||
        null;

    const title = getJigsawStoryTitle(story) || storyId;

    const bundleState = String(storyJigsaw?.state || "").toUpperCase();
    const isUnavailable = bundleState === "DISABLED" || bundleState === "DISCARDED";

    const hasFree = storyJigsaw?.free === true || jigsaw?.free === true;

    const publishedAt =
        storyJigsaw?.publishedAt ??
        story?.publishedAt ??
        ro.updatedAt ??
        null;

    const genre = story?.topic?.genre?.name ?? "default";

    const tileSize = jigsaw?.tileSize ?? resolveJigsawTemplateTileSize(jigsaw) ?? null;
    const difficultyLevel = inferJigsawDifficultyLevel(tileSize, jigsaw?.difficultyLevel ?? jigsaw?.difficulty_level);

    const completed =
        ro.isCompleted === true || ro.is_completed === true;

    const { boardPositions, scatteredPieces } = jigsawPlayLayoutFromRo(ro);

    return {
        id: storyId,
        title: "",
        type: "JIGSAW",
        ...(jigsawId ? { jigsawId } : {}),
        free: hasFree,
        isPremium: !hasFree,
        publishedAt,
        genre,
        tags: [],
        story,
        ...(storyJigsaw ? { storyJigsaw } : {}),
        ...(bundleState ? { state: bundleState } : {}),
        ...(isUnavailable ? { disabled: true } : {}),
        ...(storyJigsaw?.disabledAt != null || storyJigsaw?.disabled_at != null
            ? { disabledAt: storyJigsaw?.disabledAt ?? storyJigsaw?.disabled_at }
            : {}),
        ...(jigsaw ? { jigsaw } : {}),
        imageUrl: pickJigsawSquareImageUrl(story),
        ...(tileSize != null ? { tileSize } : {}),
        ...(difficultyLevel != null ? { difficultyLevel } : {}),
        templateName: jigsaw?.templateName ?? null,
        piecePaths: jigsaw?.piecePaths ?? null,
        svgPath: jigsaw?.svgPath ?? jigsaw?.svg_path ?? null,
        // Expose as hwPlay so PuzzleCardBase can show "Time taken"
        hwPlay: {
            isCompleted: completed,
            startedAt: ro.startedAt ?? ro.started_at ?? null,
            finishedAt: ro.finishedAt ?? ro.finished_at ?? null,
            updatedAt: ro.updatedAt ?? ro.updated_at ?? null,
        },
        jigsawPlay: {
            isCompleted: completed,
            startedAt: ro.startedAt ?? ro.started_at ?? null,
            finishedAt: ro.finishedAt ?? ro.finished_at ?? null,
            updatedAt: ro.updatedAt ?? ro.updated_at ?? null,
            attemptsCount: ro.attemptsCount ?? ro.attempts_count ?? 0,
            boardPositions,
            scatteredPieces,
        },
        boardPositions,
        scatteredPieces,
    };
}

/** Play URL for library / resume — opens the exact board when `jigsawId` is known. */
export function getJigsawPlayBoardPath(storyId, jigsawId) {
    const sid = typeof storyId === "string" ? storyId.trim() : "";
    const jid = typeof jigsawId === "string" ? jigsawId.trim() : "";
    if (!sid) return "/puzzles/jigsaw";
    if (jid) {
        return `/puzzle/play-jigsaw/${encodeURIComponent(sid)}/board/${encodeURIComponent(jid)}`;
    }
    return `/puzzle/play-jigsaw/${encodeURIComponent(sid)}`;
}
